import { Component, createRef, ReactNode, RefObject } from 'react';
import {
  applySelectionDecorators,
  focusableItems,
  selectableItems,
} from '../../../tabview/graphComponent/yfilesHelper';
import {
  addClickHandlers,
  getDefaultGraphEventHandlers,
} from '../../../tabview/graphComponent/graphEvents';
import { ZoomControls } from 'atomicComponents/Zoomable/ZoomControls';
import {
  GraphComponent,
  HierarchicalLayout,
  HierarchicalLayoutConfig,
  Command,
  Insets,
  LayoutData,
  LayoutDescriptor,
  LayoutExecutor,
  PortAdjustmentPolicy,
  TimeSpan,
} from '@ardoq/yfiles';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { ViewIds } from '@ardoq/api-types';
import { OnLayoutGraphArgs } from '../../../tabview/graphComponent/onLayoutGraph';
import { noop } from 'lodash';
import {
  FillingDiv,
  GraphContainer,
} from '../../../tabview/graphComponent/atoms';
import type { HasViewInstanceId } from '@ardoq/graph';
import {
  ZoomableViewLegendContainerForwardRef,
  ZoomContainer,
} from 'tabview/relationshipDiagrams/atoms';
import { Subscription } from 'rxjs';
import { createGraphComponent } from './yFilesUtils';
import { SimpleViewModel } from '../../types';
import { GraphInterface } from './GraphInterface';
import { isPresentationMode } from 'env';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';
import { BulkHoverPopover } from '@ardoq/popovers';
import { resolveChangedPopover } from 'scope/ChangedPopover';

interface ConfigureLayoutResult {
  layoutDescriptor: LayoutDescriptor;
  layoutData: LayoutData;
  duration?: TimeSpan;
  animateViewport?: boolean;
  updateContentRect?: boolean;
  targetBoundsInsets?: Insets;
  portAdjustmentPolicy?: PortAdjustmentPolicy;
}

interface ArdoqGraphComponentProperties extends HasViewInstanceId {
  viewModel: SimpleViewModel;
  enableStyles: boolean;
  isLegendActive: boolean;
  buildGraph: (
    graphComponent: GraphComponent,
    bypassLimit: boolean
  ) => {
    isAboveLimit: boolean;
    isEmpty: boolean;
    canBypass: boolean;
    hasLayoutUpdate: boolean;
  };
  configureLayout: (graphComponent: GraphComponent) => ConfigureLayoutResult;
  configureRendering: (graphComponent: GraphComponent) => void;
  onLayoutGraph: (args: OnLayoutGraphArgs) => void;
  viewId: ViewIds;
  additionalZoomControls?: ReactNode;
  graphInterface?: GraphInterface;
  hasZoomControls?: boolean;
  /**
   * If set to true the view will have no zoom controls, not be zoomable nor
   * pannable with the mouse.
   */
  isThumbnailView?: boolean;
  i18nLocale?: I18nLocale;
  isViewpointMode: boolean;
}
interface ArdoqGraphComponentState {
  bypassLimit: boolean;
}

export class ArdoqGraphComponent extends Component<
  ArdoqGraphComponentProperties,
  ArdoqGraphComponentState
> {
  private $graphComponent: GraphComponent | null;
  private unregisterDragAndDrop = noop;
  private cleanupPending = false;
  graphComponentContainer: RefObject<HTMLDivElement | null>;
  id: string;
  previousLayoutDescriptor: LayoutDescriptor | null = null;
  toggleLegendSubscription: Subscription | null = null;
  ZoomableViewLegendContainerForwardRef =
    createRef<React.ElementRef<typeof ZoomableViewLegendContainerForwardRef>>();
  constructor(props: ArdoqGraphComponentProperties) {
    super(props);
    this.$graphComponent = null;
    this.graphComponentContainer = createRef<HTMLDivElement>();
    this.state = { bypassLimit: false };
    this.id = ViewIds.METAMODEL; //  this.props.viewId;
  }

  componentDidMount() {
    this.cleanupPending = false;
    if (
      !this.graphComponentContainer ||
      !this.graphComponentContainer.current
    ) {
      return;
    }
    // React StrictMode double-invokes componentDidMount. The preceding
    // componentWillUnmount deferred cleanUp() via queueMicrotask, so
    // this.graphComponent is still non-null when this runs the second time.
    // Reuse the existing instance rather than trying to attach a new one to
    // the same DOM element (which yFiles rejects with an ArgumentError).
    // Setting cleanupPending = false above cancels the queued cleanup.
    if (this.graphComponent) {
      this.forceUpdate();
      return;
    }
    this.graphComponent = createGraphComponent(
      this.graphComponentContainer.current,
      this.props.isThumbnailView
    );

    this.props.graphInterface?.registerInterface({
      fitContent: () => this.graphComponent?.fitContent(),
    });

    this.props.configureRendering(this.graphComponent);
    applySelectionDecorators(this.graphComponent);
    const isViewpointMode = () => this.props.isViewpointMode;
    addClickHandlers(this.graphComponent, {
      selectableItems,
      focusableItems,
      isPresentationMode: isPresentationMode(),
      graphEventHandlers: getDefaultGraphEventHandlers({
        viewId: null,
        viewInstanceId: '',
        isViewpointMode,
      }),
      isViewpointMode,
    });

    this.forceUpdate();
  }

  componentWillUnmount() {
    this.toggleLegendSubscription?.unsubscribe();

    this.props.graphInterface?.clearInterface();

    // cleanUp() releases the yFiles GraphComponent so the host element
    // can be reused on remount. It cascades into nested root.unmount()
    // calls via yFiles disposeCallbacks; running synchronously here
    // trips React 19's "unmount during render" race. Defer past the commit.
    // cleanupPending is reset to false by componentDidMount on remount
    // (React StrictMode), which cancels this cleanup.
    this.cleanupPending = true;
    queueMicrotask(() => {
      if (!this.cleanupPending) return;
      this.graphComponent?.cleanUp();
      this.graphComponent = null;
    });
  }

  get graphComponent() {
    return this.$graphComponent;
  }

  set graphComponent(value) {
    this.$graphComponent = value;
  }

  render() {
    const {
      buildGraph,
      configureLayout,
      onLayoutGraph,
      additionalZoomControls,
      hasZoomControls = true,
      isThumbnailView = false,
    } = this.props;
    const { hasLayoutUpdate: hasGraphUpdate } = this.graphComponent
      ? buildGraph(this.graphComponent, this.state.bypassLimit)
      : {
          hasLayoutUpdate: false,
        };
    if (this.graphComponent) {
      const {
        layoutDescriptor,
        layoutData,
        duration,
        animateViewport,
        updateContentRect,
        targetBoundsInsets,
        portAdjustmentPolicy,
      } = configureLayout(this.graphComponent);

      this.graphComponent.invalidate();

      const layout = new HierarchicalLayout(
        layoutDescriptor.properties as HierarchicalLayoutConfig
      );

      new LayoutExecutor({
        graphComponent: this.graphComponent,
        layout,
        layoutData,
        animationDuration: duration,
        animateViewport,
        updateContentBounds: updateContentRect,
        targetBoundsPadding: targetBoundsInsets,
        portAdjustmentPolicies: portAdjustmentPolicy,
      })
        .start()
        .then(() => {
          if (!this.graphComponent) return;
          onLayoutGraph({
            graphComponent: this.graphComponent,
            hasGraphUpdate,
            layoutDescriptor,
            previousLayoutDescriptor: this.previousLayoutDescriptor,
          });
        });

      this.previousLayoutDescriptor = layoutDescriptor;
    }

    return (
      <FillingDiv>
        {hasZoomControls && !isThumbnailView && (
          <ZoomContainer>
            <ZoomControls
              zoomIn={() =>
                this.graphComponent?.executeCommand(Command.INCREASE_ZOOM, 1.2)
              }
              zoomOut={() =>
                this.graphComponent?.executeCommand(Command.DECREASE_ZOOM, 1.2)
              }
              zoomCenter={() =>
                this.graphComponent && this.graphComponent.fitContent()
              }
              i18nLocale={this.props.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE}
            >
              {additionalZoomControls}
            </ZoomControls>
          </ZoomContainer>
        )}
        <GraphContainer
          className="yfiles-canvascomponent"
          role="img"
          aria-label={t(
            this.props.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE,
            'viewpointBuilder:simpleGraph.ariaLabel',
            'Visualization of the graph'
          )}
          ref={this.graphComponentContainer}
          id="graphComponent"
          data-context-menu={OBJECT_CONTEXT_MENU_NAME}
        />
        <BulkHoverPopover
          rootRef={this.graphComponentContainer}
          resolveContent={resolveChangedPopover}
        />
      </FillingDiv>
    );
  }
}
